import React from 'react';
import toast from 'react-hot-toast';
import moment from 'moment';
import { Badge, Tooltip } from '@mui/material';
import {
    green,
    trakrefBlue,
    red,
} from '../../../color_utils';
import { renderWeight } from '../../../Utils/weight_utils';
import {
    ExcelIcon,
    ImageIcon,
    PDFIcon,
} from '../../../Icons';
import UserInfo from '../../../UserInfo';
import Routes from '../../../routes';

const determineBadgeColor = (status) => {
    let color = null
    const statusToLowerCase = status.toLowerCase();
    switch (statusToLowerCase) {
        case 'work completed':
        case 'operational':
            color = green
            break;
        case 'cancelled':
        case 'deactivated':
            color = '#D1D9DE'
            break;
        case 'open':
            color = '#FFBB00'
            break;
        case 'closed':
            color = trakrefBlue;
            break;
        case 'deleted':
            color = red;
            break;
        default:
            color = '#8BDC54'
            break;
    }
    return color;
};

export const exportType = {
    pdf: "pdf",
    csv: "csv",
    xlsm: "xlsm",
    xlsx: "xlsx"
};

const batchUpdateExportServiceEvents = async () => {
    fetch(Routes.ServiceEvents.GetBatchUpdateExport(), {
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        },
    })
        .then(async (response) => {
            console.log('response', response);
            const URL = await Promise.resolve(response.json());
            window.open(URL, "_blank").focus();
        })
        .catch(err => console.log('Error!', err));
};

const batchUpdateImportServiceEvents = async (file) => {
    const formattedFile = {
        fileName: file.file.name,
        fileBytes: file.data,
        contentType: file.file.type
    };
    console.log('formattedFile', formattedFile);
    fetch(Routes.ServiceEvents.BulkUpdate(), {
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        },
        method: "POST",
        body: JSON.stringify(formattedFile),
    })
        /* .then(async (response) => {
            console.log('response', response);
            const URL = await Promise.resolve(response.json());
            window.open(URL, "_blank").focus();
        }) */
        .catch(err => console.log('Error!', err));
};

export const renderStatusBadge = (statusProperty) => {
    const badgeColor = determineBadgeColor(statusProperty);
    return (
        <Tooltip
            title={statusProperty}
        >
            <Badge
                variant="dot"
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                sx={{
                    padding: '0 0 0 10%',
                    '& .MuiBadge-badge': {
                        top: '50%',
                        borderRadius: '50%',
                        backgroundColor: badgeColor,
                        color: badgeColor,
                    }
                }}
                badgeContent=""
            >
            </Badge>
        </Tooltip>
    );
};

export const determineLeakRateIcon = (epaAnnualizedLeakRate, assetTypeId) => {
    if (
        (epaAnnualizedLeakRate > 0.1 && assetTypeId === 31)
        || (epaAnnualizedLeakRate > 0.2 && assetTypeId === 22)
        || (epaAnnualizedLeakRate > 0.3 && assetTypeId === 63)
        || (epaAnnualizedLeakRate > 999999999.0)
    ) {
        return true;
    }
    return false;
};

export const getFileIcon = (fileName) => {
    const fileType = fileName.split('.').pop();
    const imageTypes = ["png", "jpg", "jpeg"];

    if (imageTypes.includes(fileType)) {
        return ImageIcon;
    }
    if (fileType === 'pdf') {
        return PDFIcon;
    }
    if (['xls', 'xlsm', 'xlsx','csv'].includes(fileType)) {
        return ExcelIcon;
    }
    // default case
    return ImageIcon;
};

export const convertDateTime = dateTime => moment(dateTime).utc().format('MM/DD/YYYY');

export const convertBoolean = boolean => boolean === true ? "Yes" : "No";

const getMimeType = (type) => {
    switch (type) {
        case exportType.pdf:
            return "application/pdf";
        case exportType.xlsm:
            return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        default:
            return "text/csv";
    }
};

const getFileName = (type, customFileName) => {
    switch (type) {
        case exportType.pdf:
            return customFileName ? `${customFileName}_${Date.now()}.pdf` : `ServiceEvents_${Date.now()}.pdf`;
        case exportType.xlsm:
            return `${customFileName}.xlsm`;
        default:
            return `${customFileName}.csv`;
    }
};

export const fetchExportData = async (e, options, fileName) => {
    e.stopPropagation();
    const {
        export_url,
        rowCount = 10,
        page = 1,
        sort,
        filters,
        type
    } = options;

    if (!export_url) return toast.error("Could not fetch export data!", { duration: 5000 });

    const response = await fetch(export_url, {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            rowCount: rowCount,
            page: page,
            sortBy: sort,
            filters: filters,
            exportType: type,
        }),
        responseType: 'blob'
    });

    const data = await response.blob();

    const customFileName = fileName || Date.now();
    const url = window.URL.createObjectURL(data, { type: getMimeType(type)});
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
        'download', 
        getFileName(type, customFileName)
    );
    document.body.appendChild(link);
    link.click();
};

export const workOrderDeleted = (column, row, link) => {
    if (column.props.isWorkOrder && UserInfo.isSystemAdministrator() === true && row[column.props.deletedProperty].toUpperCase() === 'YES') {
        return link;
    }

    return null;
};

export const getRenderValue = (type, value) => {
    let returnValue = value;
    switch (type) {
        case "dateTime":
            returnValue = value == null ? "" : convertDateTime(value);
            break;
        case "bool":
            returnValue = convertBoolean(value);
            break;
        case "percentage":
            returnValue = value === null || value === "" || value === "N/A" ? value : parseFloat(value).toFixed(UserInfo.getInstance(UserInfo.getInstanceID()).weightPrecision) + "%";
            break;
        case "weight":
            returnValue = renderWeight(value);
            break;
        default:
            break;
    }
    return returnValue;
};

export const getDefaultPage = (url, defaultPage) => {
    const possiblePage = sessionStorage.getItem(url + "page" + UserInfo.instanceId);

    if (possiblePage) {
        return JSON.parse(possiblePage);
    }

    return defaultPage;
};

export const getDefaultSortBy = (url, defaultSort) => {
    const sortKey = `${url}sort${UserInfo.instanceId}`;

    if (defaultSort) {
        sessionStorage.setItem(sortKey, JSON.stringify(defaultSort));
    }

    return JSON.parse(sessionStorage.getItem(sortKey));
};

const getSearchFilter = (property, name, keyword, isOrCondition) => ({
    property,
    name,
    type: "text",
    value: keyword,
    isOrCondition: isOrCondition
});

const createFilters = (searchById, isOrCondition, keyword, filters) => {
    if (isOrCondition) {
        return filters.map(filter => getSearchFilter(filter.field, filter.label, keyword, isOrCondition));
    } else {
        const filter = filters[searchById ? 0 : 1];
        return [getSearchFilter(filter.field, filter.label, keyword, isOrCondition)];
    }
};

const searchFilters = [
    { 
        key: 'WorkOrder', 
        check: (keyword, searchById, isOrCondition) => 
            createFilters(searchById, isOrCondition, keyword, 
            [
                { field: 'id', label: 'WORK ORDER ID' },
                { field: 'workOrderNumber', label: 'CUSTOMER WORK ORDER #' },
            ])
    },
    { 
        key: 'WorkItem', 
        check: (keyword, searchById, isOrCondition) => {
            return [getSearchFilter("idString", "SERVICE EVENT ID", keyword, isOrCondition)];
        } 
    },
    { 
        key: 'Location', 
        check: (keyword, searchById, isOrCondition) => 
            createFilters(searchById, isOrCondition, keyword, 
            [
                { field: 'code', label: 'CODE' },
                { field: 'name', label: 'LOCATION NAME' },
            ])
    },
    { 
        key: 'Asset', 
        check: (keyword, searchById, isOrCondition) => 
            createFilters(searchById, isOrCondition, keyword, 
            [
                { field: 'id', label: 'ID' },
                { field: 'name', label: 'INVENTORY NAME' },
            ])
    }
];

export const getDefaultFilters = (url, searchKeyword, searchById, isOrCondition) => {
    const filterStorageKey = `${url}filters${UserInfo.instanceId}`;

    if (searchKeyword) {
        const filter = searchFilters.find(filter => url.includes(filter.key));
        if (filter) {
            const possibleFilters = JSON.stringify(filter.check(searchKeyword, searchById, isOrCondition));
            sessionStorage.setItem(filterStorageKey, possibleFilters);

            return JSON.parse(possibleFilters);
        }
    }
    const storedFilters = sessionStorage.getItem(filterStorageKey);
    return storedFilters ? JSON.parse(storedFilters) : [];
};

export const batchUpdateExport = async (batchUpdateType) => {
    if (!batchUpdateType) return null;

    const batchUpdateFunctionTypes = {
        serviceEvents: batchUpdateExportServiceEvents
    };

    const exportFunction = batchUpdateFunctionTypes[batchUpdateType];

    return exportFunction();
};

export const batchUpdateImport = async (batchUpdateType, excelFile) => {
    if (!batchUpdateType) return null;

    const batchUpdateImportFunctionTypes = {
        serviceEvents: () => batchUpdateImportServiceEvents(excelFile)
    };

    const exportFunction = batchUpdateImportFunctionTypes[batchUpdateType];

    return exportFunction();
};

export const getFilterOptions = (field, options) => (
    options
        .filter(f => f.property === field && !!f.name && !!f.value)
        .map(f => ({ value: f.value, label: f.name }))
);

export const dataGridContainerProps = { xs: 12, md: 12, lg: 12, marginTop: 5 };